import {gql} from '@apollo/client';
import {client} from '../../../../graphql/client';

// 定义识别结果的状态码
export enum ParcelIdentificationCode {
    NO_EXIST = 'NO_EXIST',
    DELIVERED = 'DELIVERED',
    RETURNED = 'RETURNED',
    CANCELED = 'CANCELED',
    MISSING = 'MISSING',
    DSP_MISMATCH = 'DSP_MISMATCH',
    OUTSIDE_CITY_SERVICE_AREA = 'OUTSIDE_CITY_SERVICE_AREA',
    ASSIGNED_TO_CURRENT_DRIVER = 'ASSIGNED_TO_CURRENT_DRIVER',
    ASSIGNED_TO_CURRENT_UNSCANNED = 'ASSIGNED_TO_CURRENT_UNSCANNED',
    ASSIGNED_TO_CURRENT_FAILED_DELIVERY = 'ASSIGNED_TO_CURRENT_FAILED_DELIVERY',
    ASSIGNED_TO_CURRENT_TRANSFER_IN = 'ASSIGNED_TO_CURRENT_TRANSFER_IN',
    ASSIGNED_TO_CURRENT_SCANNED = 'ASSIGNED_TO_CURRENT_SCANNED',
    TRANSFER_REQUIRED = 'TRANSFER_REQUIRED',
    PARCEL_IDENTIFICATION_FAILURE = 'PARCEL_IDENTIFICATION_FAILURE',
    DRIVER_NOT_VERIFIED = 'DRIVER_NOT_VERIFIED',
    NOT_IN_YOUR_DELIVERY_AREA = 'NOT_IN_YOUR_DELIVERY_AREA',
}

// 定义包裹信息类型
export interface ParcelInfo {
    id: string;
    trackingNumber: string;
    recipientName: string;
    phone: string;
    address: string;
    routeId?: string;
    stopNumber?: string;
    subStopNumber?: string | null;
    assignedTime?: string;
    previousRouteId?: string;
    previousDriverName?: string;
    scanTime?: string;
    recommendDriverName?: string;
}

// 定义识别结果类型
export interface ParcelIdentificationResult {
    code: ParcelIdentificationCode;
    parcel?: ParcelInfo;
    errorMessage?: string;
}

// 定义转单结果类型
export interface ParcelTransferResult {
    success: boolean;
    parcel?: ParcelInfo;
    previousRouteId?: string;
    previousDriverName?: string;
    scanTime: string;
    errorMessage?: string;
}

// 定义识别和转单结果类型
export interface IdentifyAndTransferParcelResult {
    identificationResult: ParcelIdentificationResult;
    transferResult?: ParcelTransferResult;
}

// GraphQL query for parcel identification
export const IDENTIFY_PARCEL_QUERY = gql`
  query IdentifyParcel($trackingNumber: String!) {
    identifyParcel(trackingNumber: $trackingNumber) {
      code
      parcel {
        id
        trackingNumber
        recipientName
        phone
        address
        routeId
        stopNumber
        subStopNumber
        assignedTime
      }
      errorMessage
    }
  }
`;

// GraphQL mutation for identify and transfer
export const IDENTIFY_AND_TRANSFER_PARCEL_V2_MUTATION = gql`
  mutation IdentifyAndTransferParcelV2($input: IdentifyAndTransferParcelInput!) {
    identifyAndTransferParcelV2(input: $input) {
      identificationResult {
        code
        parcel {
          trackingNumber
          recipientName
          phone
          address
          stopNumber
          subStopNumber
          routeId
          assignedTime
          recommendDriverName
        }
        errorMessage
      }
      transferResult {
        success
        parcel {
          trackingNumber
          recipientName
          phone
          address
          stopNumber
          subStopNumber 
          routeId
          assignedTime
          recommendDriverName
        }
        previousRouteId
        previousDriverName
        scanTime
        errorMessage
      }
    }
  }
`;

// 包裹识别函数（第一次调用：只识别）
export const identifyParcel = async (trackingNumber: string): Promise<ParcelIdentificationResult> => {
    try {
        // 使用 GraphQL mutation，但不执行转单
        const {data, errors} = await client.mutate({
            mutation: IDENTIFY_AND_TRANSFER_PARCEL_V2_MUTATION,
            variables: {
                input: {
                    trackingNumber,
                    scanLocation: {latitude: 0, longitude: 0}, // 使用默认位置
                    confirmTransfer: false // 不执行转单，只识别
                }
            }
        });

        if (errors) {
            throw new Error(`GraphQL error: ${errors[0].message}`);
        }

        return data.identifyAndTransferParcelV2.identificationResult;
    } catch (error) {
        console.error('parcel identification error:', error);

        // 返回错误结果
        return {
            code: ParcelIdentificationCode.PARCEL_IDENTIFICATION_FAILURE,
            errorMessage: 'internet connection error, please try again',
        };
    }
};

// 包裹识别和转单函数（第二次调用：确认转单）
export const identifyAndTransferParcelV2 = async (input: {
    trackingNumber: string;
    scanLocation: { latitude: number; longitude: number };
    confirmTransfer?: boolean;
}): Promise<IdentifyAndTransferParcelResult> => {
    try {
        // 使用 GraphQL mutation
        const {data, errors} = await client.mutate({
            mutation: IDENTIFY_AND_TRANSFER_PARCEL_V2_MUTATION,
            variables: {
                input: {
                    trackingNumber: input.trackingNumber,
                    scanLocation: input.scanLocation,
                    confirmTransfer: input.confirmTransfer
                }
            }
        });

        if (errors) {
            throw new Error(`GraphQL error: ${errors[0].message}`);
        }

        return data.identifyAndTransferParcelV2;
    } catch (error) {
        console.error('parcel identification or transfer error:', error);
        // 返回错误结果
        return {
            identificationResult: {
                code: ParcelIdentificationCode.PARCEL_IDENTIFICATION_FAILURE,
                errorMessage: 'internet connection error, please try again'
            },
            transferResult: {
                success: false,
                errorMessage: 'internet connection error, please try again',
                scanTime: new Date().toISOString()
            }
        };
    }
};
